import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Bell, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface NotificationPermissionProps {
  variant?: "banner" | "inline";
}

export function NotificationPermission({ variant = "banner" }: NotificationPermissionProps) {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { permission, isSubscribed, isLoading, isSupported, isInitializing, subscribe, unsubscribe } = usePushNotifications();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || !isSupported || dismissed) return;

    // Check if user has dismissed the banner before
    const wasDismissed = localStorage.getItem("notification-banner-dismissed");
    if (wasDismissed) {
      const dismissedDate = new Date(wasDismissed);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      // Show again after 14 days
      if (daysSinceDismissed < 14) {
        setDismissed(true);
        return;
      }
    }

    // Show banner after 5 seconds if permission not granted
    const timer = setTimeout(() => {
      if (permission === "default" && !isSubscribed) {
        setShowBanner(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [user, isSupported, permission, isSubscribed, dismissed]);

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setShowBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setDismissed(true);
    localStorage.setItem("notification-banner-dismissed", new Date().toISOString());
  };

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };

  // Inline variant for settings pages
  if (variant === "inline") {
    if (!isSupported) {
      return (
        <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
          <div className="flex items-center gap-3">
            <BellOff className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-medium text-foreground">{t("pwa.notificationsTitle")}</p>
              <p className="text-sm text-muted-foreground">
                {t("pwa.notificationsUnsupported")}
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
        <div className="flex items-center gap-3">
          <Bell className={`w-5 h-5 ${isSubscribed ? "text-primary" : "text-muted-foreground"}`} />
          <div>
            <p className="font-medium text-foreground">{t("pwa.notificationsPushTitle")}</p>
            <p className="text-sm text-muted-foreground">
              {isSubscribed
                ? t("pwa.notificationsSubscribed")
                : t("pwa.notificationsUnsubscribed")}
            </p>
          </div>
        </div>
        <Switch
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={isLoading || isInitializing}
        />
      </div>
    );
  }

  // Banner variant
  if (!user || !isSupported || isSubscribed || permission === "denied") {
    return null;
  }

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-20 left-4 right-4 z-40 md:left-auto md:right-4 md:max-w-sm"
        >
          <div className="bg-card border border-border rounded-2xl p-4 shadow-lg">
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Bell className="w-6 h-6 text-primary" />
              </div>

              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-foreground mb-1">
                  {t("pwa.enableTitle")}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {t("pwa.enableDescription")}
                </p>

                <div className="flex gap-2">
                  <Button onClick={handleEnable} size="sm" disabled={isLoading}>
                    {isLoading ? t("pwa.enablingButton") : t("pwa.enableButton")}
                  </Button>
                  <Button onClick={handleDismiss} variant="ghost" size="sm">
                    {t("pwa.notNow")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
